import React from 'react';
import { T } from '../tokens';
import useTemplateEditor from '../hooks/useTemplateEditor';
import FlowToolbar from './FlowToolbar';
import FlowPreviewBar from './FlowPreviewBar';
import FlowStepLibrary from './FlowStepLibrary';
import FlowCanvas from './FlowCanvas';
import NodeEditorPanel from './NodeEditorPanel';

export default function FlowBuilder({
  template,
  settings,
  isNew = false,
  onClose,
  onSave,
  onPreview,
  onSettingsChange,
  saving,
  saveError,
  onClearSaveError,
}) {
  const editor = useTemplateEditor({ template, settings, onPreview, isNew });

  const handleSave = async () => {
    if (!editor.validate()) return;
    const payload = editor.buildSavePayload();
    await onSave(payload);
  };

  const handleSettingsChange = (patch) => {
    editor.updateSettings(patch);
    onSettingsChange?.(patch);
  };

  return (
    <div
      className="sma-fade-in"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(11,18,32,0.45)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', zIndex: 1300, padding: 24,
      }}
    >
      <div style={{
        width: 'min(1180px, 100%)', height: 'min(760px, 92vh)', background: T.page, borderRadius: 18,
        overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 30px 80px -20px rgba(11,18,32,0.5)',
      }}
      >
        <FlowToolbar
          template={template}
          isNew={isNew}
          onPreview={editor.runPreview}
          onSave={handleSave}
          onClose={onClose}
          previewLoading={editor.previewLoading}
          saving={saving}
          isValid={editor.isValid}
          saveError={saveError}
          onClearSaveError={onClearSaveError}
        />

        <FlowPreviewBar preview={editor.preview} />

        <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '210px 1fr 300px', minHeight: 0 }}>
          <FlowStepLibrary
            selectedTriggerId={editor.selectedTriggerId}
            onTriggerSelect={editor.setTriggerFromLibrary}
          />
          <FlowCanvas
            flow={editor.flow}
            selectedNode={editor.selectedNode}
            onSelectNode={editor.setSelectedNode}
            onReorderNode={editor.reorderFlow}
            onInsertFromLibrary={editor.insertFromLibrary}
          />
          <div style={{ borderLeft: `1px solid ${T.line}`, background: T.card, overflowY: 'auto' }}>
            <NodeEditorPanel
              node={editor.selectedNode}
              draft={editor.draft}
              isNew={isNew}
              onClose={() => editor.setSelectedNode(null)}
              onBodyChange={editor.updateBody}
              onDmBodyChange={editor.updateDmBody}
              onDraftChange={editor.updateDraft}
              settings={editor.localSettings}
              onSettingsChange={handleSettingsChange}
              errors={editor.errors}
              onSaveStep={editor.saveStep}
              stepSaved={editor.stepSaved}
              onRemoveStep={editor.removeSelectedNode}
              canRemoveStep={editor.canRemoveSelected}
              removeBlockReason={editor.removeBlockReason}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
